import React, { memo, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, ModalBody, ModalFooter, ModalHeader, Table } from 'reactstrap';

import Button from 'components/App/Button';
import { formatFlightAmount, formatFlightDateTime } from 'constants/flightBooking';

const AMOUNT_COLUMNS = [
  { key: 'baseFare', label: 'Giá vé' },
  { key: 'totalTax', label: 'Thuế' },
  { key: 'refundFee', label: 'Phí hoàn' },
  { key: 'usedFare', label: 'Đã sử dụng' },
  { key: 'refundFare', label: 'Được hoàn' },
  { key: 'refundTotal', label: 'Tổng hoàn' },
];

// Step 2 of the refund: shows the partner quote, then confirms it by quoteId.
function ModalFlightRefund(props) {
  const { quote, isLoading, onRecalculate, onConfirm, onClose } = props;

  const [isExpired, setIsExpired] = useState(false);

  useEffect(() => {
    setIsExpired(false);
    if (!quote?.quoteExpiresAt) return undefined;

    const timer = setTimeout(
      () => setIsExpired(true),
      new Date(quote.quoteExpiresAt).getTime() - Date.now(),
    );
    return () => clearTimeout(timer);
  }, [quote]);

  const currency = quote?.currency;

  return (
    <Modal isOpen={!!quote} toggle={onClose} centered size="xl">
      <ModalHeader toggle={onClose}>Hoàn vé với đối tác</ModalHeader>
      <ModalBody>
        {quote && (
          <>
            <div className="table-responsive">
              <Table size="sm" className="align-middle">
                <thead>
                  <tr>
                    <th>Số vé</th>
                    <th>Hành khách</th>
                    {AMOUNT_COLUMNS.map((column) => <th key={column.key}>{column.label}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {(quote.breakdown || []).map((row) => (
                    <tr key={row.ticketNumber}>
                      <td>{row.ticketNumber}</td>
                      <td>{row.passengerName || '-'}</td>
                      {AMOUNT_COLUMNS.map((column) => (
                        <td key={column.key}>{formatFlightAmount(row[column.key], currency)}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
            <p className="fw-bold mb-1">
              {`Tổng hoàn từ đối tác: ${formatFlightAmount(quote.refundTotal, currency)}`}
            </p>
            <p className={isExpired ? 'text-danger mb-0' : 'text-muted mb-0'}>
              {isExpired
                ? 'Báo giá đã hết hạn, vui lòng tính lại.'
                : `Báo giá hết hạn lúc ${formatFlightDateTime(quote.quoteExpiresAt)}`}
            </p>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button outline onClick={onClose}>Huỷ</Button>
        {isExpired ? (
          <Button loading={isLoading} disabled={isLoading} onClick={onRecalculate}>Tính lại</Button>
        ) : (
          <Button loading={isLoading} disabled={isLoading} onClick={() => onConfirm(quote.quoteId)}>
            Xác nhận hoàn vé
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
}

ModalFlightRefund.propTypes = {
  quote: PropTypes.shape({
    quoteId: PropTypes.string,
    quoteExpiresAt: PropTypes.string,
    currency: PropTypes.string,
    refundTotal: PropTypes.number,
    breakdown: PropTypes.arrayOf(PropTypes.shape({
      ticketNumber: PropTypes.string,
      passengerName: PropTypes.string,
    })),
  }),
  isLoading: PropTypes.bool,
  onRecalculate: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

ModalFlightRefund.defaultProps = {
  quote: null,
  isLoading: false,
};

export default memo(ModalFlightRefund);
